/**
 * Cheesy Store: reproductor de audio compartido.
 *
 * Un solo HTMLAudioElement para el botón flotante y la botonera: al tocar otro sonido, el anterior
 * se detiene. Nada suena solo: cada reproducción nace de un clic o una tecla. No pide micrófono.
 * Volumen, silencio y efectos se recuerdan en localStorage, sin más datos.
 *
 * Efectos (Web Audio, se arma con el primer sonido): audio > filtro de tono > maestro > analizador,
 * con un eco (retardo con retroalimentación) en paralelo. La velocidad cambia también el tono, como un
 * vinilo. Los audios vienen del CDN de Shopify, que permite CORS (crossOrigin anonymous).
 * <chz-knob> es una perilla accesible (role slider): se gira arrastrando, con las flechas o con
 * Inicio y Fin; doble clic vuelve al valor de fábrica. La botonera dibuja el ecualizador con el
 * analizador mientras suena.
 */

const VOLUME_KEY = 'chz-audio-volume';
const MUTED_KEY = 'chz-audio-muted';
const FX_KEY = 'chz-audio-fx';

function readNumber(key, fallback) {
  try {
    const value = parseFloat(localStorage.getItem(key) ?? '');
    return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : fallback;
  } catch {
    return fallback;
  }
}

function readFlag(key) {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function store(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* sin almacenamiento */
  }
}

/** Estado global único. */
const state = {
  /** @type {HTMLAudioElement | null} */
  audio: null,
  /** @type {{id: string, title: string, url: string, transcript: string} | null} */
  current: null,
  status: 'idle', // idle | loading | playing | error
  /** Quién pidió el sonido ('fab' o 'sampler'): cada control muestra solo lo que reprodujo. */
  source: '',
  volume: readNumber(VOLUME_KEY, 0.8),
  muted: readFlag(MUTED_KEY),
};

/** Efectos: tono 0..1 (1 = abierto), velocidad 0.5..1.6, eco 0..1. */
const fx = {
  /** @type {AudioContext | null} */ ctx: null,
  /** @type {GainNode | null} */ master: null,
  /** @type {BiquadFilterNode | null} */ filter: null,
  /** @type {GainNode | null} */ feedback: null,
  /** @type {GainNode | null} */ wet: null,
  /** @type {AnalyserNode | null} */ analyser: null,
  tone: 1,
  speed: 1,
  echo: 0,
};
try {
  const saved = JSON.parse(localStorage.getItem(FX_KEY) || '{}');
  for (const key of ['tone', 'speed', 'echo']) {
    if (Number.isFinite(saved[key])) fx[key] = saved[key];
  }
} catch {
  /* sin almacenamiento */
}

function emit() {
  document.dispatchEvent(new CustomEvent('chz-audio:state', { detail: { ...state, audio: undefined } }));
}

function getAudio() {
  if (state.audio) return state.audio;
  const audio = new Audio();
  audio.crossOrigin = 'anonymous';
  audio.preload = 'none';
  audio.volume = state.volume;
  audio.muted = state.muted;
  audio.addEventListener('playing', () => {
    state.status = 'playing';
    emit();
  });
  audio.addEventListener('ended', () => {
    state.status = 'idle';
    emit();
  });
  audio.addEventListener('error', () => {
    if (!state.current) return;
    state.status = 'error';
    emit();
  });
  state.audio = audio;
  return audio;
}

/** Arma el grafo de efectos la primera vez (siempre dentro de un clic o una tecla). */
function ensureGraph() {
  if (fx.ctx) {
    if (fx.ctx.state === 'suspended') fx.ctx.resume();
    return;
  }
  const Ctx = window.AudioContext || /** @type {any} */ (window).webkitAudioContext;
  if (!Ctx) return;
  try {
    const audio = getAudio();
    const ctx = new Ctx();
    const source = ctx.createMediaElementSource(audio);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.Q.value = 0.9;
    const master = ctx.createGain();
    const delay = ctx.createDelay(1);
    delay.delayTime.value = 0.23;
    const feedback = ctx.createGain();
    const wet = ctx.createGain();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 64;
    analyser.smoothingTimeConstant = 0.72;
    source.connect(filter);
    filter.connect(master);
    filter.connect(delay);
    delay.connect(feedback);
    feedback.connect(delay);
    delay.connect(wet);
    wet.connect(master);
    master.connect(analyser);
    analyser.connect(ctx.destination);
    Object.assign(fx, { ctx, master, filter, feedback, wet, analyser });
  } catch {
    fx.ctx = null;
  }
  applyFx();
}

function applyFx() {
  const audio = state.audio;
  if (audio) {
    // Cargar otro archivo devuelve playbackRate a defaultPlaybackRate: con los dos, la velocidad
    // elegida vale también para el próximo sonido.
    audio.defaultPlaybackRate = fx.speed;
    audio.playbackRate = fx.speed;
    audio.preservesPitch = false;
    /** @type {any} */ (audio).webkitPreservesPitch = false;
    audio.muted = state.muted;
  }
  if (!fx.ctx || !fx.master || !fx.filter || !fx.wet || !fx.feedback) {
    if (audio) audio.volume = state.volume;
    return;
  }
  if (audio) audio.volume = 1; // con el grafo, el volumen lo maneja el maestro
  const now = fx.ctx.currentTime;
  fx.master.gain.setTargetAtTime(state.volume, now, 0.02);
  fx.filter.frequency.setTargetAtTime(350 * Math.pow(18000 / 350, fx.tone), now, 0.02);
  fx.wet.gain.setTargetAtTime(fx.echo * 0.7, now, 0.03);
  fx.feedback.gain.setTargetAtTime(fx.echo * 0.55, now, 0.03);
}

/** @param {'tone' | 'speed' | 'echo'} name @param {number} value */
export function setFx(name, value) {
  fx[name] = value;
  applyFx();
  store(FX_KEY, JSON.stringify({ tone: fx.tone, speed: fx.speed, echo: fx.echo }));
  emit();
}

export function getFx() {
  return { tone: fx.tone, speed: fx.speed, echo: fx.echo, volume: state.volume };
}

export async function play(sound, source = '') {
  const audio = getAudio();
  ensureGraph();
  audio.pause();
  state.current = sound;
  state.source = source;
  state.status = 'loading';
  emit();
  try {
    if (!sound?.url) throw new Error('missing url');
    audio.src = sound.url;
    await audio.play();
  } catch (error) {
    if (error?.name === 'AbortError') return; // otro sonido tomó el turno
    state.status = 'error';
    emit();
  }
}

export function stop() {
  if (state.audio) {
    state.audio.pause();
    state.audio.currentTime = 0;
  }
  state.status = 'idle';
  emit();
}

export function setVolume(value) {
  state.volume = Math.min(1, Math.max(0, value));
  applyFx();
  store(VOLUME_KEY, String(state.volume));
  emit();
}

export function setMuted(muted) {
  state.muted = muted;
  applyFx();
  store(MUTED_KEY, muted ? '1' : '0');
  emit();
}

/** Perilla accesible. data-fx: volume | tone | speed | echo; data-min, data-max, data-step, data-default. */
class ChzKnob extends HTMLElement {
  #abort = new AbortController();
  #value = 0;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const min = this.#num('min', 0);
    const max = this.#num('max', 1);
    this.setAttribute('role', 'slider');
    this.tabIndex = 0;
    this.setAttribute('aria-valuemin', String(min));
    this.setAttribute('aria-valuemax', String(max));
    this.#set(this.#current(), false);

    let start = 0;
    let startX = 0;
    let startY = 0;
    this.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      this.setPointerCapture(event.pointerId);
      start = this.#value;
      startX = event.clientX;
      startY = event.clientY;
      this.focus();
    }, { signal });
    this.addEventListener('pointermove', (event) => {
      if (!this.hasPointerCapture(event.pointerId)) return;
      const delta = (event.clientX - startX - (event.clientY - startY)) / 160;
      this.#set(start + delta * (max - min), true);
    }, { signal });
    this.addEventListener('keydown', (event) => {
      const step = this.#num('step', 0.05);
      const keys = { ArrowRight: step, ArrowUp: step, ArrowLeft: -step, ArrowDown: -step, PageUp: step * 5, PageDown: -step * 5 };
      if (event.key in keys) this.#set(this.#value + keys[/** @type {keyof typeof keys} */ (event.key)], true);
      else if (event.key === 'Home') this.#set(min, true);
      else if (event.key === 'End') this.#set(max, true);
      else return;
      event.preventDefault();
    }, { signal });
    this.addEventListener('dblclick', () => this.#set(this.#num('default', min), true), { signal });
    document.addEventListener('chz-audio:state', () => {
      if (this.dataset.fx === 'volume' && !this.matches(':focus')) this.#set(state.volume, false);
    }, { signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** @param {string} key @param {number} fallback */
  #num(key, fallback) {
    const value = parseFloat(this.dataset[key] ?? '');
    return Number.isFinite(value) ? value : fallback;
  }

  #current() {
    const name = this.dataset.fx;
    if (name === 'volume') return state.volume;
    if (name === 'tone' || name === 'speed' || name === 'echo') return fx[name];
    return this.#num('default', 0);
  }

  /** @param {number} raw @param {boolean} commit */
  #set(raw, commit) {
    const min = this.#num('min', 0);
    const max = this.#num('max', 1);
    const step = this.#num('step', 0.05);
    const value = Math.min(max, Math.max(min, Math.round(raw / step) * step));
    this.#value = value;
    const ratio = (value - min) / (max - min || 1);
    this.style.setProperty('--chz-knob-angle', `${-135 + ratio * 270}deg`);
    this.setAttribute('aria-valuenow', value.toFixed(2));
    const name = this.dataset.fx;
    this.setAttribute('aria-valuetext', name === 'speed' ? `${value.toFixed(2)}x` : `${Math.round(ratio * 100)}%`);
    if (!commit) return;
    if (name === 'volume') setVolume(value);
    else if (name === 'tone' || name === 'speed' || name === 'echo') setFx(name, value);
  }
}

/** @param {Element} root */
function readSounds(root) {
  const node = root.querySelector('script[type="application/json"][data-chz-sounds]');
  try {
    const list = JSON.parse(node?.textContent || '[]');
    return list.filter((s) => s && s.url).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  } catch {
    return [];
  }
}

/** Botón flotante: reproduce uno al azar, sin repetir el anterior si hay más de uno. */
class ChzAudioFab extends HTMLElement {
  #abort = new AbortController();
  #sounds = [];
  #lastId = '';

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#sounds = readSounds(this);
    this.querySelector('[data-chz-audio-play]')?.addEventListener('click', () => this.#playRandom(), { signal });
    this.querySelector('[data-chz-audio-stop]')?.addEventListener('click', () => stop(), { signal });
    this.querySelector('[data-chz-audio-mute]')?.addEventListener('click', () => setMuted(!state.muted), { signal });
    const range = this.querySelector('[data-chz-audio-volume]');
    if (range instanceof HTMLInputElement) {
      range.value = String(Math.round(state.volume * 100));
      range.addEventListener('input', () => setVolume(Number(range.value) / 100), { signal });
    }
    document.addEventListener('chz-audio:state', () => this.#render(), { signal });
    this.#render();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  #playRandom() {
    if (this.#sounds.length === 0) return;
    let pool = this.#sounds;
    if (pool.length > 1) pool = pool.filter((s) => s.id !== this.#lastId);
    const sound = pool[Math.floor(Math.random() * pool.length)];
    this.#lastId = sound.id;
    play(sound, 'fab');
  }

  #render() {
    // El panel flotante solo se abre con lo que pidió el botón flotante; la botonera tiene su propia pantalla.
    const active = state.source === 'fab' && state.current && this.#sounds.some((s) => s.id === state.current.id);
    this.dataset.status = active ? state.status : 'idle';
    this.toggleAttribute('data-muted', state.muted);
    const status = this.querySelector('[data-chz-audio-status]');
    const transcript = this.querySelector('[data-chz-audio-transcript]');
    if (status) {
      if (active && state.status === 'error') status.textContent = this.dataset.labelError || '';
      else if (active && (state.status === 'playing' || state.status === 'loading'))
        status.textContent = (this.dataset.labelPlaying || '').replace('[title]', state.current.title);
      else status.textContent = '';
    }
    if (transcript) transcript.textContent = active && state.current.transcript ? state.current.transcript : '';
    const mute = this.querySelector('[data-chz-audio-mute]');
    if (mute) mute.setAttribute('aria-label', state.muted ? this.dataset.labelUnmute || '' : this.dataset.labelMute || '');
  }
}

/** Botonera: un pad por sonido, con estado visible al pulsar. */
class ChzSampler extends HTMLElement {
  #abort = new AbortController();
  #sounds = new Map();

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    for (const sound of readSounds(this)) this.#sounds.set(sound.id, sound);
    this.addEventListener(
      'click',
      (event) => {
        const pad = event.target instanceof Element ? event.target.closest('[data-chz-pad]') : null;
        if (pad instanceof HTMLElement) {
          const sound = this.#sounds.get(pad.dataset.chzPad || '');
          if (sound) play(sound, 'sampler');
          return;
        }
        if (event.target instanceof Element && event.target.closest('[data-chz-audio-stop]')) stop();
        if (event.target instanceof Element && event.target.closest('[data-chz-audio-mute]')) setMuted(!state.muted);
      },
      { signal }
    );
    const range = this.querySelector('[data-chz-audio-volume]');
    if (range instanceof HTMLInputElement) {
      range.value = String(Math.round(state.volume * 100));
      range.addEventListener('input', () => setVolume(Number(range.value) / 100), { signal });
    }
    document.addEventListener('chz-audio:state', () => this.#render(), { signal });
    this.#render();
    this.#eq = [...this.querySelectorAll('[data-chz-eq] span')];
  }

  disconnectedCallback() {
    this.#abort.abort();
    window.cancelAnimationFrame(this.#frame);
  }

  /** @type {Element[]} */
  #eq = [];
  #frame = 0;
  #bins = new Uint8Array(32);

  /** Ecualizador: barras que siguen al analizador mientras suena; en reposo quedan bajitas. */
  #drawEq = () => {
    const playing = state.status === 'playing' && state.source === 'sampler';
    const analyser = fx.analyser;
    if (playing && analyser) analyser.getByteFrequencyData(this.#bins);
    const per = Math.max(1, Math.floor(this.#bins.length / Math.max(1, this.#eq.length)));
    this.#eq.forEach((bar, i) => {
      let level = 0.08;
      if (playing && analyser) {
        let sum = 0;
        for (let k = 0; k < per; k++) sum += this.#bins[i * per + k] || 0;
        level = Math.max(0.08, Math.min(1, sum / per / 230));
      }
      /** @type {HTMLElement} */ (bar).style.transform = `scaleY(${level.toFixed(3)})`;
    });
    if (playing) this.#frame = window.requestAnimationFrame(this.#drawEq);
    else this.#frame = 0;
  };

  #render() {
    if (this.#eq.length && !this.#frame && state.status === 'playing' && state.source === 'sampler') {
      this.#frame = window.requestAnimationFrame(this.#drawEq);
    } else if (this.#eq.length && state.status !== 'playing') {
      this.#drawEq();
    }
    const currentId = state.current?.id;
    for (const pad of this.querySelectorAll('[data-chz-pad]')) {
      const isCurrent = pad instanceof HTMLElement && pad.dataset.chzPad === currentId;
      pad.toggleAttribute('data-active', Boolean(isCurrent && (state.status === 'playing' || state.status === 'loading')));
      pad.toggleAttribute('data-error', Boolean(isCurrent && state.status === 'error'));
      pad.setAttribute('aria-pressed', String(Boolean(isCurrent && state.status === 'playing')));
    }
    const screen = this.querySelector('[data-chz-audio-status]');
    const transcript = this.querySelector('[data-chz-audio-transcript]');
    const mine = currentId && this.#sounds.has(currentId);
    if (screen) {
      if (mine && state.status === 'error') screen.textContent = this.dataset.labelError || '';
      else if (mine && state.status !== 'idle') screen.textContent = (this.dataset.labelPlaying || '').replace('[title]', state.current.title);
      else screen.textContent = this.dataset.labelIdle || '';
    }
    if (transcript) transcript.textContent = mine && state.status !== 'idle' ? state.current.transcript || '' : '';
    this.toggleAttribute('data-muted', state.muted);
    const mute = this.querySelector('[data-chz-audio-mute]');
    if (mute) mute.setAttribute('aria-label', state.muted ? this.dataset.labelUnmute || '' : this.dataset.labelMute || '');
  }
}

if (!customElements.get('chz-audio-fab')) customElements.define('chz-audio-fab', ChzAudioFab);
if (!customElements.get('chz-sampler')) customElements.define('chz-sampler', ChzSampler);
if (!customElements.get('chz-knob')) customElements.define('chz-knob', ChzKnob);
